import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Filter, SlidersHorizontal, Search, X } from "lucide-react";
import { api, CITIES } from "@/lib/api";
import DoctorCard from "@/components/DoctorCard";

export default function Doctors() {
  const [sp, setSp] = useSearchParams();
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [specialties, setSpecialties] = useState([]);

  const f = {
    q: sp.get("q") || "",
    specialty: sp.get("specialty") || "",
    city: sp.get("city") || "",
    gender: sp.get("gender") || "",
    min_experience: sp.get("min_experience") || "",
    max_fee: sp.get("max_fee") || "",
    min_rating: sp.get("min_rating") || "",
    sort: sp.get("sort") || "rating",
  };

  useEffect(() => {
    api.get("/specialties").then((r) => setSpecialties(r.data));
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = {};
    Object.entries(f).forEach(([k, v]) => { if (v) params[k] = v; });
    api.get("/doctors", { params }).then((r) => setDoctors(r.data)).finally(() => setLoading(false));
  }, [sp]); // eslint-disable-line

  const update = (key, value) => {
    const next = new URLSearchParams(sp);
    if (value) next.set(key, value); else next.delete(key);
    setSp(next, { replace: true });
  };

  const clearAll = () => setSp(new URLSearchParams(), { replace: true });

  const Filters = () => (
    <div className="space-y-5">
      <div>
        <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Specialty</label>
        <select data-testid="filter-specialty" className="input-field mt-2" value={f.specialty} onChange={(e) => update("specialty", e.target.value)}>
          <option value="">All</option>
          {specialties.map((s) => <option key={s.name}>{s.name}</option>)}
        </select>
      </div>
      <div>
        <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">City</label>
        <select data-testid="filter-city" className="input-field mt-2" value={f.city} onChange={(e) => update("city", e.target.value)}>
          <option value="">All</option>
          {CITIES.map((c) => <option key={c}>{c}</option>)}
        </select>
      </div>
      <div>
        <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Max Fee (₹)</label>
        <select data-testid="filter-max-fee" className="input-field mt-2" value={f.max_fee} onChange={(e) => update("max_fee", e.target.value)}>
          <option value="">Any</option>
          <option value="500">Under ₹500</option>
          <option value="750">Under ₹750</option>
          <option value="1000">Under ₹1000</option>
          <option value="2000">Under ₹2000</option>
        </select>
      </div>
      <div>
        <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Min Experience</label>
        <select data-testid="filter-experience" className="input-field mt-2" value={f.min_experience} onChange={(e) => update("min_experience", e.target.value)}>
          <option value="">Any</option>
          <option value="5">5+ years</option>
          <option value="10">10+ years</option>
          <option value="15">15+ years</option>
        </select>
      </div>
      <div>
        <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Gender</label>
        <div className="mt-2 flex gap-2">
          {["", "male", "female"].map((g) => (
            <button
              key={g || "any"}
              data-testid={`filter-gender-${g || "any"}`}
              onClick={() => update("gender", g)}
              className={`flex-1 rounded-xl border px-3 py-2 text-sm font-medium capitalize transition-colors ${
                f.gender === g ? "border-blue-700 bg-blue-50 text-blue-700" : "border-slate-200 text-slate-700 hover:border-slate-300"
              }`}
            >
              {g || "Any"}
            </button>
          ))}
        </div>
      </div>
      <div>
        <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Min Rating</label>
        <select data-testid="filter-rating" className="input-field mt-2" value={f.min_rating} onChange={(e) => update("min_rating", e.target.value)}>
          <option value="">Any</option>
          <option value="4.5">4.5+ ★</option>
          <option value="4.0">4.0+ ★</option>
          <option value="3.5">3.5+ ★</option>
        </select>
      </div>
      <button data-testid="filter-clear-btn" onClick={clearAll} className="w-full text-sm font-semibold text-blue-700 hover:text-blue-800">Clear all filters</button>
    </div>
  );

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">Find a Doctor</h1>
          <p className="mt-1 text-sm text-slate-600">
            {loading ? "Searching…" : `${doctors.length} verified doctor${doctors.length !== 1 ? "s" : ""} found`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select data-testid="sort-select" className="input-field !w-auto" value={f.sort} onChange={(e) => update("sort", e.target.value)}>
            <option value="rating">Top Rated</option>
            <option value="experience">Most Experienced</option>
            <option value="fee_low">Fee: Low to High</option>
            <option value="fee_high">Fee: High to Low</option>
          </select>
          <button data-testid="mobile-filter-btn" onClick={() => setShowFilters(true)} className="btn-outline !py-2 lg:hidden">
            <SlidersHorizontal className="h-4 w-4" /> Filters
          </button>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-[280px_1fr]">
        <aside className="hidden lg:block">
          <div className="card sticky top-24 p-6">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-blue-700" />
              <div className="text-sm font-bold text-slate-900">Filters</div>
            </div>
            <div className="mt-5"><Filters /></div>
          </div>
        </aside>

        <div>
          <div className="mb-5 relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="search-q-input"
              className="input-field pl-9"
              placeholder="Search by name, specialty or clinic…"
              value={f.q}
              onChange={(e) => update("q", e.target.value)}
            />
          </div>

          {loading ? (
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="card h-64 animate-pulse p-6" />
              ))}
            </div>
          ) : doctors.length === 0 ? (
            <div className="card flex flex-col items-center justify-center p-16 text-center">
              <div className="text-5xl">🔎</div>
              <div className="mt-4 text-lg font-bold text-slate-900">No doctors match your filters</div>
              <p className="mt-1 text-sm text-slate-600">Try removing some filters or searching a different city.</p>
              <button onClick={clearAll} className="btn-primary mt-5">Clear filters</button>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-2">
              {doctors.map((d) => <DoctorCard key={d.id} doctor={d} />)}
            </div>
          )}
        </div>
      </div>

      {/* Mobile filter drawer */}
      {showFilters && (
        <div className="fixed inset-0 z-50 bg-black/40 lg:hidden" onClick={() => setShowFilters(false)}>
          <div className="absolute right-0 top-0 h-full w-[85vw] max-w-sm overflow-y-auto bg-white p-6" onClick={(e) => e.stopPropagation()}>
            <div className="mb-4 flex items-center justify-between">
              <div className="text-lg font-bold">Filters</div>
              <button onClick={() => setShowFilters(false)} className="rounded-full p-2 hover:bg-slate-100"><X className="h-5 w-5" /></button>
            </div>
            <Filters />
            <button onClick={() => setShowFilters(false)} className="btn-primary mt-6 w-full">Apply</button>
          </div>
        </div>
      )}
    </div>
  );
}
